import { logger } from '@/services/logger'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import isEqual from 'lodash/isEqual'
import {
  getCompanyOrdersByCompanyId,
  type CompanyOrdersQueryParams
} from '@/services/companyOrders'
import type { CompanyOrderType } from '@/types/companyOrders.types'
import { useEmployeeStore } from '@/stores/employee'
import axios from 'axios'

export const useCompanyOrdersStore = defineStore('company-orders', () => {
  const companyOrders = ref<CompanyOrderType[]>([])
  const lastCompanyOrders = ref<CompanyOrderType[]>([])
  const getCompanyOrdersPendingPromise = ref<Promise<void> | null>(null)
  const lastQueryParams = ref<CompanyOrdersQueryParams | undefined>(undefined)
  const currentAbortController = ref<AbortController | null>(null)

  const getCompanyOrders = async (queryParams?: CompanyOrdersQueryParams) => {
    if (getCompanyOrdersPendingPromise.value) {
      const isSameQueryParams = isEqual(queryParams, lastQueryParams.value)

      if (isSameQueryParams) {
        await getCompanyOrdersPendingPromise.value

        return
      }

      currentAbortController.value?.abort()
      getCompanyOrdersPendingPromise.value = null
    }

    lastQueryParams.value = queryParams
    currentAbortController.value = new AbortController()

    const promise = executeGetCompanyOrders(queryParams, {
      signal: currentAbortController.value.signal
    })

    getCompanyOrdersPendingPromise.value = promise

    promise.finally(() => {
      if (getCompanyOrdersPendingPromise.value === promise) {
        getCompanyOrdersPendingPromise.value = null
      }
    })

    await promise
  }

  const executeGetCompanyOrders = async (
    queryParams?: CompanyOrdersQueryParams,
    options?: { signal?: AbortSignal }
  ) => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')

      const response = await getCompanyOrdersByCompanyId(
        employeeStore.employee.companyId,
        queryParams,
        options?.signal
      )

      if (!response.data || response.status !== 200) {
        throw new Error('Failed to get company orders')
      }

      companyOrders.value = response.data.orders
    } catch (error) {
      if (axios.isCancel(error)) {
        return
      }

      logger.error(error)

      throw error
    }
  }

  const getLastCompanyOrders = async (queryParams?: CompanyOrdersQueryParams) => {
    const employeeStore = useEmployeeStore()

    try {
      if (!employeeStore.employee) {
        await employeeStore.setEmployee()
      }

      if (!employeeStore.employee?.companyId) throw new Error('Company ID not found')
      const response = await getCompanyOrdersByCompanyId(
        employeeStore.employee.companyId,
        queryParams
      )

      if (!response.data || response.status !== 200) {
        throw new Error('Failed to get last company orders')
      }

      lastCompanyOrders.value = response.data.orders
    } catch (error) {
      logger.error(error)

      throw error
    }
  }

  return {
    companyOrders,
    lastCompanyOrders,
    getCompanyOrders,
    getLastCompanyOrders
  }
})
